// ============================================================================
// DRONETAG CLOUD API CLIENT
// Pull drone detections from Dronetag's Cloud REST API
// ============================================================================

export interface DronetagPosition {
  operationId: string;
  serialNumber?: string;
  timestamp: Date;
  latitude: number;
  longitude: number;
  altitudeMSL: number;
  altitudeAGL?: number;
  groundSpeed: number;
  verticalSpeed?: number;
  heading: number;
  operationalState?: string;
  uaType?: number;
  operatorId?: string;
  registration?: string;
  accuracy?: number;
}

interface DronetagTeleUa {
  type: 'tele-ua';
  timestamp: string;
  operation_id: string;
  serial_number?: string;
  registration?: string;
  location: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  altitudes?: Array<{
    value: number;
    type?: string;
  }>;
  velocity?: {
    horizontal_speed?: number;
    vertical_speed?: number;
    heading?: number;
  };
  operational_state?: string;
  ua_type?: number;
  operator_id?: string;
}

interface DronetagApiResponse {
  data?: DronetagTeleUa[];
  // NDJSON response is handled separately
}

const DRONETAG_API_BASE = 'https://api.dronetag.app/v2';

/**
 * Fetch drone telemetry from the Dronetag Cloud API.
 */
export async function fetchDronetagTelemetry(
  apiToken: string,
  bbox?: { minLat: number; maxLat: number; minLng: number; maxLng: number },
): Promise<DronetagPosition[]> {
  const params = new URLSearchParams();
  params.set('format', 'json');

  if (bbox) {
    params.set('bbox', `${bbox.minLng},${bbox.minLat},${bbox.maxLng},${bbox.maxLat}`);
  }

  const response = await fetch(`${DRONETAG_API_BASE}/airspace/telemetry/global-ua?${params}`, {
    headers: {
      'X-Personal-Access-Token': apiToken,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Dronetag API error: ${response.status} ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type') || '';
  let messages: DronetagTeleUa[];

  if (contentType.includes('application/x-ndjson') || contentType.includes('text/plain')) {
    // NDJSON response
    const text = await response.text();
    messages = text
      .split('\n')
      .filter((line) => line.trim())
      .map((line) => {
        try { return JSON.parse(line); }
        catch { return null; }
      })
      .filter((msg): msg is DronetagTeleUa => msg !== null && msg.type === 'tele-ua');
  } else {
    // JSON response
    const data: DronetagApiResponse = await response.json();
    messages = (data.data || []).filter((msg) => msg.type === 'tele-ua');
  }

  return messages
    .filter((msg) => msg.location && msg.location.latitude !== 0 && msg.location.longitude !== 0)
    .map((msg) => parseTeleUa(msg));
}

function parseTeleUa(msg: DronetagTeleUa): DronetagPosition {
  const wgs84Alt = msg.altitudes?.find((a) => a.type === 'WGS84');
  const aglAlt = msg.altitudes?.find((a) => a.type === 'AGL');
  const fallbackAlt = msg.altitudes?.[0];

  return {
    operationId: msg.operation_id,
    serialNumber: msg.serial_number,
    timestamp: new Date(msg.timestamp),
    latitude: msg.location.latitude,
    longitude: msg.location.longitude,
    altitudeMSL: wgs84Alt?.value ?? fallbackAlt?.value ?? 0,
    altitudeAGL: aglAlt?.value,
    groundSpeed: msg.velocity?.horizontal_speed ?? 0,
    verticalSpeed: msg.velocity?.vertical_speed,
    heading: msg.velocity?.heading ?? 0,
    operationalState: msg.operational_state,
    uaType: msg.ua_type,
    operatorId: msg.operator_id,
    registration: msg.registration,
    accuracy: msg.location.accuracy,
  };
}
